import { useEffect, type JSX } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, Layers } from 'lucide-react';
import { useSessionStore } from '@/stores/session';
import { NO_AREA_LABEL, useAreaSelection } from '@/hooks/use-area-selection';
import { type ProductArea } from '@/lib/schemas/product-area.schema';
import { cn } from '@/lib/utils';
import { BAR_TEXT_BUTTON_CLASS } from '@/components/layout/bar-styles';

// Selector global de "área" (cliente con fieldPriorityConfig). Cuando hay un
// área elegida, el back aplica su config para resolver campo por campo qué
// fuente gana — así dos pantallas muestran el mismo valor del mismo producto.
//
// Sin área elegida = comportamiento actual (default+schema explícito del
// front). El toggle de área es persistente por browser (localStorage).
//
// Si el endpoint `/products/areas` no existe todavía (back sin deployar) el
// hook falla silencioso y no renderizamos el selector — cero ruptura.
//
// Recovery del slug persistido: en el próximo release, las áreas se van a
// scopear por usuario. Un slug guardado que ya no está en el listado del
// usuario devolvería 403 en cada request. Validamos on-mount contra la
// lista fresh:
// - No está: se dropea. Si queda una sola en el listado, se autoselecciona;
//   si hay varias, queda sin selección hasta que el user elija.
//
// En el celular el área se elige desde el menú del avatar (`Navbar`), pero
// este componente sigue montado, oculto: el efecto de recovery vive acá y
// tiene que correr en cualquier tamaño de pantalla. Lista y reglas de cuándo
// se ofrece salen de `useAreaSelection`, compartido con ese menú.
export const AreaSelector = (): JSX.Element | null => {
  const { areas, activeSlug, activeName, setActiveArea, isLoading, isSuccess, isAvailable } =
    useAreaSelection();

  const user = useSessionStore((s) => s.user);
  const canBypass = user?.canBypassAreas ?? true;
  const userAreaSlugs = user?.areaSlugs;

  useEffect(() => {
    if (!isSuccess) return;
    // Autoselect obligatorio (release 2026-08-24): un user scoped con una
    // sola area asignada Y sin bypass NO debe navegar sin `area=`. El back
    // aplica la única area silencioso al backend, pero forzamos el selector
    // a coincidir para que la UI sea coherente.
    //
    // Esa única área se deja elegida aunque no venga en la lista (área mal
    // configurada o borrada): si el chequeo de abajo la quitara, este bloque
    // la volvería a poner, sin fin, y la app caía con "Maximum update depth
    // exceeded". El back responde lo que corresponda a esa área.
    if (!canBypass && userAreaSlugs && userAreaSlugs.length === 1) {
      if (activeSlug !== userAreaSlugs[0]) setActiveArea(userAreaSlugs[0] ?? null);
      return;
    }
    if (activeSlug === null) return;
    const found = areas.some((a) => a.slug === activeSlug);
    if (found) return;
    // Slug persistido no está en la lista actual. Autoselect si queda una;
    // sino, dejar sin selección.
    setActiveArea(areas.length === 1 ? (areas[0]?.slug ?? null) : null);
  }, [isSuccess, areas, activeSlug, setActiveArea, canBypass, userAreaSlugs]);

  if (!isAvailable) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          data-tour="navbar-area"
          disabled={isLoading}
          className={cn(BAR_TEXT_BUTTON_CLASS, 'disabled:cursor-not-allowed disabled:opacity-60')}
          aria-label="Cambiar área activa"
        >
          <Layers className="h-4 w-4" aria-hidden="true" />
          <span className="hidden max-w-40 truncate sm:inline">{activeName}</span>
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className={cn(
            'border-border bg-surface text-text z-50 min-w-56 overflow-hidden rounded-md border p-1 shadow-md',
            'animate-in fade-in-0 zoom-in-95',
          )}
        >
          <DropdownMenu.Label className="text-text-muted px-3 py-2 text-xs tracking-wide uppercase">
            Área activa
          </DropdownMenu.Label>
          <AreaMenuItems areas={areas} activeSlug={activeSlug} onSelect={setActiveArea} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

interface AreaMenuItemsProps {
  areas: readonly ProductArea[];
  activeSlug: string | null;
  onSelect: (slug: string | null) => void;
}

// Opciones del selector: "Sin área", el catálogo con la activa marcada y la
// nota de qué hace el área. Las usan este dropdown y el submenú "Área" del
// menú de usuario en el celular, así los dos ofrecen exactamente lo mismo.
export const AreaMenuItems = ({ areas, activeSlug, onSelect }: AreaMenuItemsProps): JSX.Element => {
  return (
    <>
      <DropdownMenu.Item
        onSelect={() => onSelect(null)}
        className={cn(
          'data-highlighted:bg-surface-2 flex cursor-pointer items-center justify-between gap-2 rounded-sm px-3 py-2 text-sm outline-hidden transition-colors select-none',
          activeSlug === null && 'text-text font-medium',
        )}
      >
        <span>{NO_AREA_LABEL}</span>
        {activeSlug === null && <Check className="text-primary h-4 w-4" aria-hidden="true" />}
      </DropdownMenu.Item>
      <DropdownMenu.Separator className="bg-border my-1 h-px" />
      {areas.map((a) => (
        <DropdownMenu.Item
          key={a.slug}
          onSelect={() => onSelect(a.slug)}
          className={cn(
            'data-highlighted:bg-surface-2 flex cursor-pointer items-center justify-between gap-2 rounded-sm px-3 py-2 text-sm outline-hidden transition-colors select-none',
            activeSlug === a.slug && 'text-text font-medium',
          )}
        >
          <span className="min-w-0 truncate">{a.name}</span>
          {activeSlug === a.slug && <Check className="text-primary h-4 w-4" aria-hidden="true" />}
        </DropdownMenu.Item>
      ))}
      <DropdownMenu.Separator className="bg-border my-1 h-px" />
      <div className="text-text-muted px-3 py-2 text-[10px]">
        El área define qué fuente gana campo por campo. Cambia lo que ves en todas las pantallas.
      </div>
    </>
  );
};
